/**
 * Central TanStack Query keys — use these for cache hits, invalidation, and prefetch.
 */
export const queryKeys = {
  projects: {
    all: ['projects'] as const,
    list: () => [...queryKeys.projects.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.projects.all, 'detail', id] as const,
  },
  scheduler: {
    all: ['scheduler'] as const,
    participants: (projectId: string) =>
      [...queryKeys.scheduler.all, 'participants', projectId] as const,
    schedulers: (projectId: string) =>
      [...queryKeys.scheduler.all, 'schedulers', projectId] as const,
  },
  /** Global moderators directory (same payload from schedulesApi / schedulerApi). */
  moderators: {
    all: ['moderators'] as const,
    list: () => [...queryKeys.moderators.all, 'list'] as const,
  },
  /** Detailed session types per project (scheduling UI). */
  sessionTypes: {
    all: ['sessionTypes'] as const,
    detailedByProject: (projectId: string) =>
      [...queryKeys.sessionTypes.all, 'detailed', projectId] as const,
  },
  /** Participant / session / project change-log (History tabs). scheduleId scopes to one session. */
  history: {
    all: ['history'] as const,
    forParticipant: (participantId: string, projectId: string, scheduleId?: string) =>
      [...queryKeys.history.all, participantId, projectId, scheduleId ?? null] as const,
    /** Session type change-log (OD-892 "History" tab). Base key for useServerPagination. */
    forSessionType: (sessionTypeId: string) =>
      [...queryKeys.history.all, 'sessionType', sessionTypeId] as const,
    /** Moderator availability change-log (OD-892 "View History"). Base key for useServerPagination. */
    forModeratorAvailability: (moderatorId: string, projectId: string) =>
      [...queryKeys.history.all, 'moderatorAvailability', moderatorId, projectId] as const,
    forProject: (projectId: string) =>
      [...queryKeys.history.all, 'project', projectId] as const,
    /** Team member change-log (OD-1144 "History" tab). Project-wide, not per-member. */
    forTeam: (projectId: string) =>
      [...queryKeys.history.all, 'team', projectId] as const,
    /** Email template change-log (OD-1146 "History" tab). */
    forEmailTemplates: (projectId: string) =>
      [...queryKeys.history.all, 'emailTemplates', projectId] as const,
  },
  /** OneForma tasks available to attach to a session type, per project. */
  tasks: {
    all: ['tasks'] as const,
    byProject: (projectId: string) =>
      [...queryKeys.tasks.all, 'byProject', projectId] as const,
    /** Single task record (Task Management module — create/edit/duplicate). */
    detail: (id: string) => [...queryKeys.tasks.all, 'detail', id] as const,
  },
  /** OneForma jobs available to attach to a session type, per project. */
  jobs: {
    all: ['jobs'] as const,
    byProject: (projectId: string) =>
      [...queryKeys.jobs.all, 'byProject', projectId] as const,
  },
  /** Moderator availability records, per project (scheduling UI). */
  moderatorAvailability: {
    all: ['moderatorAvailability'] as const,
    byProject: (projectId: string) =>
      [...queryKeys.moderatorAvailability.all, 'byProject', projectId] as const,
  },
  /** Weekly recurring schedules feeding the moderator-availability calendar, per project. */
  schedules: {
    all: ['schedules'] as const,
    weeklyByProject: (projectId: string) =>
      [...queryKeys.schedules.all, 'weekly', projectId] as const,
    /** Single schedule record. Only cached where the read isn't live/check-in-status-sensitive. */
    detail: (id: string) => [...queryKeys.schedules.all, 'detail', id] as const,
  },
  /** Team Management: members + stats, and the "any scheduled sessions?" gate check. */
  team: {
    all: ['team'] as const,
    byProject: (projectId: string) =>
      [...queryKeys.team.all, 'byProject', projectId] as const,
    hasScheduledSessions: (projectId: string) =>
      [...queryKeys.team.all, 'hasScheduledSessions', projectId] as const,
  },
  /**
   * Full-project demographic questions + per-participant answers (Participants
   * grid columns + CSV export). Response depends only on projectId — not
   * page/search/sort/filters — so this is the entire key.
   */
  participantsDemographics: {
    all: ['participantsDemographics'] as const,
    byProject: (projectId: string) =>
      [...queryKeys.participantsDemographics.all, projectId] as const,
  },
  /**
   * Action Center nav-badge + page failed-payment count. Shared by the
   * sidebar badge (mounted on every page) and the Action Center page itself
   * so they don't independently re-fetch the same payment-stats data. The
   * schedule-derived metrics (no-primary-moderator / upcoming / no-show /
   * moderator rollup) reuse `schedules.weeklyByProject` instead — see
   * useActionCenterDataQuery.ts.
   */
  actionCenter: {
    all: ['actionCenter'] as const,
    paymentStats: (projectId: string) =>
      [...queryKeys.actionCenter.all, 'paymentStats', projectId] as const,
    /** The plain-request (unflagged) pending count backing the Pending
     * Payment Requests card — a distinct server call (`bonus_flag=false`),
     * so it gets its own cache entry rather than sharing paymentStats'. */
    plainPendingPaymentStats: (projectId: string) =>
      [...queryKeys.actionCenter.all, 'plainPendingPaymentStats', projectId] as const,
    bonusFlags: (projectId: string) =>
      [...queryKeys.actionCenter.all, 'bonusFlags', projectId] as const,
    sessionTypes: (projectId: string) =>
      [...queryKeys.actionCenter.all, 'sessionTypes', projectId] as const,
    /** OD-1102 follow-up: PM allow/block decisions on no-show/cancellation-limit participants. */
    bookingDecisions: (projectId: string) =>
      [...queryKeys.actionCenter.all, 'bookingDecisions', projectId] as const,
    /** The current moderator's own add/remove events on upcoming sessions. */
    mySessionChanges: (projectId: string) =>
      [...queryKeys.actionCenter.all, 'mySessionChanges', projectId] as const,
    /** OD-1259: pending moderator-availability approvals for the PM card/badge. */
    availabilityApprovals: (projectId: string) =>
      [...queryKeys.actionCenter.all, 'availabilityApprovals', projectId] as const,
  },
} as const;
